// Email reminder subscriptions — feature logic (docs/reminders.md).
//
// The API primitives live in data.ts; this module owns the role-derived option
// lists, the derived display status, and form validation. Only steps the user
// can act on are offered. A notification is Scheduled (a digest on its days at
// its time) or Triggered (every fifteen minutes, the invoices that arrived at
// its steps since the last run, D-391, D-395).
//
// Two things the port changed. Editing is copy-on-write, and that is now ONE
// server call (`replaceReminder`): the old client created the replacement, then
// superseded the original, and had to retry the second write and warn about a
// possible duplicate when it failed. And the day, workspace and step lists are
// real arrays on the wire instead of comma-joined strings.
import { TIMELINE_NODES, WORKSPACES, type NodeKey, type Workspace } from './constants'
import { scopeSession, availableWorkspaces, type Session } from './identity'
import { createReminder, replaceReminder, setReminderStatus } from './data'
import type { ReminderSubscription, ReminderWrite } from './types'
import { sentenceList } from './format'

/** What makes a notification send (owner, 2026-10-21 — D-391). */
export type NotificationKind = 'scheduled' | 'triggered'

/** The Type field's choices, in the order it offers them. */
export const KIND_OPTIONS: readonly { value: NotificationKind; label: string }[] = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'triggered', label: 'Triggered' },
]

/** A row stored before D-391 has no kind on the wire; it is Scheduled. */
function kindOf(row: Pick<ReminderSubscription, 'kind'>): NotificationKind {
  return row.kind === 'triggered' ? 'triggered' : 'scheduled'
}

// Day tokens the scheduler matches against (`ddd` in Eastern time).
export const DAY_TOKENS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

// Quarter-hour options (owner, 2026-10-14: "searchable with fifteen-minute
// increments") — the scheduler ticks every fifteen minutes, so that is the
// finest granularity, and the form must not imply minute precision.
export const TIME_OPTIONS: { value: string; label: string }[] = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4)
  const m = String((i % 4) * 15).padStart(2, '0')
  const h12 = h % 12 === 0 ? 12 : h % 12
  return { value: `${String(h).padStart(2, '0')}:${m}`, label: `${h12}:${m} ${h < 12 ? 'AM' : 'PM'}` }
})

/** `HH:MM`, dropping any seconds the wire carries, so a stored `09:00:00`
    still matches an option. */
function normalizeTime(time: string | undefined | null): string {
  return (time ?? '').slice(0, 5)
}

export function timeLabel(time: string | undefined | null): string {
  const t = normalizeTime(time)
  return TIME_OPTIONS.find((o) => o.value === t)?.label ?? t
}

/** The time options a typed search keeps. Punctuation and spaces are ignored,
    so `830`, `8:30` and `8:30 a` all find 8:30 AM. */
export function searchTimeOptions(term: string): { value: string; label: string }[] {
  const squash = (x: string) => x.toLowerCase().replace(/[^0-9a-z]/g, '')
  const q = squash(term)
  return q === '' ? TIME_OPTIONS : TIME_OPTIONS.filter((o) => squash(o.label).startsWith(q))
}

// ---- Options derived from the user's roles ---------------------------------

export interface StepOption {
  key: NodeKey
  label: string
  /** Workspaces (of the user's available set) where this step is actionable. */
  workspaces: Workspace[]
}

/** Pipeline steps the user can act on, unioned across their available
    workspaces (Archives excluded — it is a lookup, not a waiting queue). The
    step keys are the hyphenated node keys, which are exactly the tokens the
    subscription stores. */
export function stepOptions(session: Session): StepOption[] {
  const byKey = new Map<NodeKey, StepOption>()
  for (const ws of availableWorkspaces(session)) {
    const roles = new Set(scopeSession(session, ws).roles)
    for (const n of TIMELINE_NODES) {
      if (n.archive) continue
      if (!roles.has(n.role)) continue
      const existing = byKey.get(n.key)
      if (existing) existing.workspaces.push(ws)
      else byKey.set(n.key, { key: n.key, label: n.label, workspaces: [ws] })
    }
  }
  return TIMELINE_NODES.filter((n) => byKey.has(n.key)).map((n) => byKey.get(n.key)!)
}

// ---- Display status ------------------------------------------------------
/** ACTIVE OR INACTIVE, the usual toggle (owner, 2026-10-14 — D-361). Active
    sends; Inactive is the stored `paused`. There is no date window any more,
    so there is no *Not started* or *Ended*. */
export function isActiveSubscription(row: ReminderSubscription): boolean {
  return row.status !== 'paused'
}

/**
 * The days, as a reader says them: "Every Day", "Mon–Fri", "Sat and Sun", or a
 * list in week order with runs of three or more collapsed, joined with the
 * Oxford comma — "Mon and Wed–Fri", "Mon, Wed, and Fri".
 */
export function formatDays(days: readonly string[]): string {
  const picked = DAY_TOKENS.filter((d) => days.includes(d))
  if (picked.length === 0) return 'No Days'
  if (picked.length === 7) return 'Every Day'
  const index = picked.map((d) => DAY_TOKENS.indexOf(d))
  const runs: string[][] = []
  index.forEach((i, n) => {
    if (n > 0 && i === (index[n - 1] as number) + 1) runs[runs.length - 1]!.push(DAY_TOKENS[i] as string)
    else runs.push([DAY_TOKENS[i] as string])
  })
  const parts = runs.flatMap((r) => (r.length >= 3 ? [`${r[0]}–${r[r.length - 1]}`] : r))
  return sentenceList(parts)
}

/** "Mon–Fri at 9:00 AM", or "When An Invoice Arrives" for a Triggered one. */
export function formatSchedule(row: ReminderSubscription): string {
  if (kindOf(row) === 'triggered') return 'When An Invoice Arrives'
  return `${formatDays(row.days_of_week ?? [])} at ${timeLabel(row.time_of_day)}`
}

// ---- Form model ------------------------------------------------------------
export interface ReminderForm {
  /** Empty until the Type is chosen; the fields under it wait for it (D-391). */
  kind: NotificationKind | ''
  title: string
  days: string[]
  time: string
  /** Fixed to the one workspace. The picker is gone (a one-option control), but
      the value is still submitted — the API requires at least one. */
  workspaces: Workspace[]
  steps: NodeKey[]
}

export function emptyForm(): ReminderForm {
  return {
    kind: '',
    title: '',
    days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    time: '09:00',
    workspaces: [...WORKSPACES],
    steps: [],
  }
}

export function rowToForm(row: ReminderSubscription): ReminderForm {
  const stored = (row.workspaces ?? []).filter((w): w is Workspace =>
    (WORKSPACES as readonly string[]).includes(w),
  )
  const kind = kindOf(row)
  return {
    kind,
    title: row.title ?? '',
    // A Triggered row has no schedule; switching it to Scheduled starts from the usual one.
    days: kind === 'triggered' ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] : [...(row.days_of_week ?? [])],
    time: normalizeTime(row.time_of_day) || '09:00',
    workspaces: stored.length > 0 ? stored : [...WORKSPACES],
    steps: [...(row.steps ?? [])] as NodeKey[],
  }
}

export function validateForm(form: ReminderForm): string | null {
  if (form.kind === '') return 'Choose a type.'
  if (form.workspaces.length === 0) return 'Choose at least one document type.'
  if (form.steps.length === 0) return 'Choose at least one step.'
  // A Triggered notification has no schedule to check.
  if (form.kind === 'triggered') return null
  if (form.days.length === 0) return 'Choose at least one day of the week.'
  if (!form.time) return 'Choose a time of day.'
  // The scheduler compares the quarter hour, so 8:20 would fire at 8:15. The
  // API rejects it too (400); this is the friendlier version of the same rule.
  if (!/^\d{2}:(00|15|30|45)$/.test(normalizeTime(form.time))) return 'Choose a time on the quarter hour.'
  return null
}

function formToWrite(form: ReminderForm): ReminderWrite {
  const scheduled = form.kind !== 'triggered'
  return {
    title: form.title.trim() || null,
    kind: scheduled ? 'scheduled' : 'triggered',
    days_of_week: scheduled ? form.days : null,
    time_of_day: scheduled ? normalizeTime(form.time) : null,
    workspaces: form.workspaces,
    steps: form.steps,
  }
}

// ---- Writes ----------------------------------------------------------------
// The server owns the subscriber (it knows the caller), the lineage, and the
// last-sent stamp carried across an edit so a mid-day change cannot double-send.

export async function saveNewSubscription(form: ReminderForm): Promise<void> {
  await createReminder(formToWrite(form))
}

/** The pop-up's title for what it shows (owner, 2026-11-02 — D-454): the list,
    or the form, titled as an Admin's editor on Assignments → Notifications is. */
export function remindersTitle(mode: 'list' | 'form', editing: boolean): string {
  if (mode === 'list') return 'Custom Notifications'
  return editing ? 'Edit Custom Notification' : 'Add Custom Notification'
}

/** The person's name as Assignments → Notifications shows it: their name, else their address. */
export const subscriberName = (row: Pick<ReminderSubscription, 'subscriber_name' | 'subscriber_email'>) =>
  row.subscriber_name || row.subscriber_email

/** Someone a notification is written for: an Admin's pick (D-454). */
export interface Subscriber {
  id: string
  name: string | null
  email: string
}

/** An Admin's create for someone else (D-454). The server checks the steps
    against that person's access, not the Admin's. */
export async function saveNewSubscriptionFor(form: ReminderForm, subscriber: Subscriber): Promise<void> {
  await createReminder({
    ...formToWrite(form),
    subscriber_id: subscriber.id,
    subscriber_name: subscriber.name,
    subscriber_email: subscriber.email,
  })
}

/** A person's steps as the form offers them, from the keys the server allows
    (D-454), in the pipeline's order. */
export function stepOptionsFromKeys(keys: readonly string[]): StepOption[] {
  return TIMELINE_NODES.filter((n) => !n.archive && keys.includes(n.key)).map((n) => ({
    key: n.key,
    label: n.label,
    workspaces: [...WORKSPACES],
  }))
}

/** Editing is copy-on-write, in one server transaction: the replacement is
    written and the original superseded together, so a failure leaves the old
    version live rather than losing both. */
export async function saveEditedSubscription(
  old: ReminderSubscription,
  form: ReminderForm,
  active: boolean = old.status !== 'paused',
): Promise<void> {
  const { subscription } = await replaceReminder(old.id, { ...formToWrite(form), row_version: old.row_version })
  // The new version inherits the old one's on/off state; the toggle changes it
  // with the status route only when it was switched (D-361).
  if (active !== (subscription.status !== 'paused')) await setReminderStatus(subscription.id, active ? 'active' : 'paused')
}

export async function cancelSubscription(row: ReminderSubscription): Promise<void> {
  await setReminderStatus(row.id, 'cancelled')
}
